import { useCallback, useEffect, useState } from 'react';
import { challengeApi } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import './AdminChallengesPage.css';

const GOAL_KEYS = [
  { key: 'bible_completions', label: 'Complete read-throughs', unit: 'read-throughs' },
  { key: 'chapters_read', label: 'Chapters read together', unit: 'chapters' },
  { key: 'chapters_covered', label: 'Chapters of the Bible reached', unit: 'chapters' }
];

const blankChallenge = () => ({
  name: '',
  slug: '',
  startDate: '',
  endDate: '',
  description: '',
  isActive: true
});

/**
 * Create and edit challenges and their global goals (WS4).
 *
 * A challenge is only "running" when it is active AND today falls inside its
 * window, which is what the home hero keys off — so this page shows that
 * status explicitly rather than just the isActive flag.
 */
const AdminChallengesPage = () => {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(blankChallenge());
  const [editingId, setEditingId] = useState(null);
  const [goalDrafts, setGoalDrafts] = useState({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await challengeApi.getAllForAdmin();
      setChallenges(response.data.challenges || []);
      setError(null);
    } catch (err) {
      console.error('Failed to load challenges:', err);
      setError(err.response?.data?.error || 'Failed to load challenges.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Local calendar day — UTC toISOString() flips the badge after evening in the US.
  const today = new Intl.DateTimeFormat('en-CA').format(new Date());
  const asDateOnly = (value) => (value ? String(value).split('T')[0] : '');
  const isRunning = (c) => c.isActive && asDateOnly(c.startDate) <= today && today <= asDateOnly(c.endDate);

  const startEdit = (challenge) => {
    setEditingId(challenge.id);
    setForm({
      name: challenge.name,
      slug: challenge.slug,
      startDate: asDateOnly(challenge.startDate),
      endDate: asDateOnly(challenge.endDate),
      description: challenge.description || '',
      isActive: challenge.isActive
    });
    setMessage('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(blankChallenge());
  };

  const submit = async (event) => {
    event.preventDefault();
    if (form.endDate < form.startDate) {
      setError('End date must not be before start date.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      if (editingId) {
        await challengeApi.updateChallenge(editingId, form);
        setMessage(`Saved "${form.name}".`);
      } else {
        await challengeApi.createChallenge(form);
        setMessage(`Created "${form.name}".`);
      }
      cancelEdit();
      await load();
    } catch (err) {
      console.error('Failed to save challenge:', err);
      setError(err.response?.data?.error
        || err.response?.data?.errors?.[0]?.msg
        || 'Failed to save challenge.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (challenge) => {
    if (!window.confirm(`Delete "${challenge.name}"? Its goals go with it. Reading reports are not affected.`)) {
      return;
    }
    try {
      await challengeApi.deleteChallenge(challenge.id);
      setMessage(`Deleted "${challenge.name}".`);
      await load();
    } catch (err) {
      console.error('Failed to delete challenge:', err);
      setError(err.response?.data?.error || 'Failed to delete challenge.');
    }
  };

  const saveGoal = async (challengeId, goalKey) => {
    const draftKey = `${challengeId}:${goalKey}`;
    const raw = goalDrafts[draftKey];
    const targetValue = parseInt(raw, 10);
    if (Number.isNaN(targetValue) || targetValue < 0) {
      setError('Target must be a number of 0 or more.');
      return;
    }

    const meta = GOAL_KEYS.find(g => g.key === goalKey);
    try {
      await challengeApi.saveGoal(challengeId, {
        key: goalKey,
        targetValue,
        name: meta.label,
        unit: meta.unit,
        displayOrder: GOAL_KEYS.findIndex(g => g.key === goalKey) + 1
      });
      setMessage(`Saved ${meta.label} target.`);
      setGoalDrafts(prev => ({ ...prev, [draftKey]: undefined }));
      await load();
    } catch (err) {
      console.error('Failed to save goal:', err);
      setError(err.response?.data?.error || 'Failed to save goal.');
    }
  };

  if (loading) return <LoadingSpinner size="large" message="Loading challenges..." />;

  return (
    <div className="admin-challenges">
      <div className="admin-challenges-header">
        <h1>Challenges</h1>
        <p>
          A challenge gives the community a season with a start, an end, and a countdown.
          Only one runs at a time: the one that is active and whose window contains today.
        </p>
      </div>

      {error && <div className="admin-challenges-error">{error}</div>}
      {message && <div className="admin-challenges-message">{message}</div>}

      <form className="challenge-form" onSubmit={submit}>
        <h2>{editingId ? 'Edit challenge' : 'New challenge'}</h2>

        <div className="challenge-form-row">
          <label>
            Name
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Reformation Reading 2026"
              required
              maxLength={200}
            />
          </label>
          <label>
            Slug
            <input
              type="text"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder="reformation-reading-2026"
              pattern="[a-z0-9-]+"
              title="Lowercase letters, numbers and hyphens"
              required
            />
          </label>
        </div>

        <div className="challenge-form-row">
          <label>
            Starts
            <input
              type="date"
              value={form.startDate}
              onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              required
            />
          </label>
          <label>
            Ends
            <input
              type="date"
              value={form.endDate}
              onChange={(e) => setForm({ ...form, endDate: e.target.value })}
              required
            />
          </label>
          <label className="challenge-active-toggle">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
            Active
          </label>
        </div>

        <label>
          Description
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2}
            maxLength={2000}
            placeholder="Read the Bible through together during October."
          />
        </label>

        <div className="challenge-form-actions">
          <button type="submit" disabled={saving}>
            {saving ? 'Saving…' : editingId ? 'Save changes' : 'Create challenge'}
          </button>
          {editingId && (
            <button type="button" className="secondary" onClick={cancelEdit}>Cancel</button>
          )}
        </div>
      </form>

      <div className="challenge-list">
        {challenges.length === 0 && <p className="challenge-empty">No challenges yet.</p>}

        {challenges.map(challenge => {
          const globalGoals = (challenge.goals || []).filter(g => !g.churchId);
          return (
            <div key={challenge.id} className={`challenge-card ${isRunning(challenge) ? 'running' : ''}`}>
              <div className="challenge-card-head">
                <div>
                  <h3>{challenge.name}</h3>
                  <p className="challenge-window">
                    {asDateOnly(challenge.startDate)} → {asDateOnly(challenge.endDate)}
                    {' · '}
                    <code>{challenge.slug}</code>
                  </p>
                </div>
                <div className="challenge-status">
                  {isRunning(challenge)
                    ? <span className="badge running">Running now</span>
                    : challenge.isActive
                      ? <span className="badge scheduled">
                          {asDateOnly(challenge.startDate) > today ? 'Scheduled' : 'Ended'}
                        </span>
                      : <span className="badge off">Inactive</span>}
                </div>
              </div>

              <div className="challenge-goals-editor">
                {GOAL_KEYS.map(meta => {
                  const existing = globalGoals.find(g => g.key === meta.key);
                  const draftKey = `${challenge.id}:${meta.key}`;
                  const value = goalDrafts[draftKey] ?? (existing ? String(existing.targetValue) : '');
                  return (
                    <div key={meta.key} className="challenge-goal-row">
                      <span className="goal-label">{meta.label}</span>
                      <input
                        type="number"
                        min="0"
                        value={value}
                        placeholder="no target"
                        onChange={(e) => setGoalDrafts(prev => ({ ...prev, [draftKey]: e.target.value }))}
                      />
                      <button
                        type="button"
                        onClick={() => saveGoal(challenge.id, meta.key)}
                        disabled={value === '' || value === (existing ? String(existing.targetValue) : '')}
                      >
                        Save
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="challenge-card-actions">
                <button type="button" onClick={() => startEdit(challenge)}>Edit</button>
                <button type="button" className="danger" onClick={() => remove(challenge)}>Delete</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminChallengesPage;
